import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  coachCountColumn,
  createdColumn,
  DescriptionField,
  DRAFT_PENDING_LINK_CLASS,
  previewColumn,
  ROW_LINK_CLASS,
  statusColumn,
  TitleSlugFields,
  useAdminListCreateState,
} from "~/components/cih/admin-list-page";
import { Badge } from "~/components/ui/badge";
import { Card } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/cn";
import {
  ccoQueryKeys,
  cihCcosQueryOptions,
  createCihCcoFn,
  type CihCcoListRow,
} from "~/lib/functions/cih/ccos";
import { CCOType } from "~/lib/schemas/db-enums";
import {
  COACH_COUNT_SORTS,
  coachCountSearchSchema,
  type CoachCountSort,
  nextCoachCountSortSearch,
  resolveCoachCountSort,
  sortByCoachCount,
} from "~/lib/shared/cih-coach-count-sort";

export const Route = createFileRoute("/admin/cih/ccos/")({
  staticData: { title: "Organizations" },
  validateSearch: coachCountSearchSchema,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(cihCcosQueryOptions());
    return {};
  },
  component: CcosPage,
});

const CCO_TYPE_COLORS: Record<CCOType, string> = {
  [CCOType.PAO]: "bg-violet-100 text-violet-700 border-violet-200",
  [CCOType.CTO]: "bg-sky-100 text-sky-700 border-sky-200",
};

function TypeBadge({ type }: { type: string }) {
  const colorClass =
    type === CCOType.PAO || type === CCOType.CTO
      ? CCO_TYPE_COLORS[type]
      : "bg-gray-100 text-gray-700";
  return (
    <Badge size="sm" className={cn("uppercase", colorClass)}>
      {type}
    </Badge>
  );
}

interface CreateFormState {
  name: string;
  slug: string;
  type: "pao" | "cto";
  description: string;
}

const EMPTY_FORM: CreateFormState = {
  name: "",
  slug: "",
  type: "cto",
  description: "",
};

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: CCOType.PAO, label: "PAO" },
  { value: CCOType.CTO, label: "CTO" },
] as const;

type TypeFilterValue = (typeof TYPE_FILTER_OPTIONS)[number]["value"];

const CCO_COLUMNS: ColumnDef<CihCcoListRow>[] = [
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => (
      <Link
        to="/admin/cih/ccos/$ccoId"
        params={{ ccoId: row.original.id }}
        className={ROW_LINK_CLASS}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => <TypeBadge type={row.original.type} />,
  },
  {
    id: "subcategory",
    header: "Subcategory",
    cell: ({ row }) => (
      <span className="text-gray-600">
        {row.original.subcategory ?? "\u2014"}
      </span>
    ),
  },
  statusColumn(
    (row) =>
      row.pendingDraftId && (
        <Link
          to="/admin/cih/ccos/$ccoId"
          params={{ ccoId: row.pendingDraftId }}
          className={DRAFT_PENDING_LINK_CLASS}
        >
          {row.pendingDraftStatus === "in_review"
            ? "Waiting for review"
            : "Draft pending"}
        </Link>
      ),
  ),
  coachCountColumn(),
  {
    id: "programs",
    header: "Programs",
    meta: { align: "end" },
    cell: ({ row }) => (
      <span className="text-gray-600 tabular-nums">
        {row.original.ctpCount}
      </span>
    ),
  },
  createdColumn(),
  previewColumn((row) =>
    row.status === "published" ? `/organizations/${row.slug}` : null,
  ),
];

function CcosPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const { sort, direction } = resolveCoachCountSort(Route.useSearch());
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilterValue>("all");
  const [query, setQuery] = useState("");
  const { create, form, setFormField } = useAdminListCreateState(EMPTY_FORM);

  const { data: ccos, isLoading } = useQuery(cihCcosQueryOptions());

  const totalCount = ccos?.length ?? 0;
  const paoCount = ccos?.filter((c) => c.type === CCOType.PAO).length ?? 0;
  const ctoCount = ccos?.filter((c) => c.type === CCOType.CTO).length ?? 0;

  const createMutation = useMutation({
    mutationFn: createCihCcoFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ccoQueryKeys.all });
      create.finish("Organization created.");
      navigate({
        to: "/admin/cih/ccos/$ccoId",
        params: { ccoId: created.id },
      });
    },
  });

  const handleSubmit = (): void => {
    if (!form.name.trim() || !form.slug.trim()) return;
    createMutation.mutate({
      data: {
        name: form.name.trim(),
        slug: form.slug.trim(),
        type: form.type,
        description: form.description.trim() || undefined,
      },
    });
  };

  const shownCcos = useMemo((): CihCcoListRow[] => {
    if (!ccos) return [];
    const q = query.toLowerCase();
    const matches = ccos.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (typeFilter !== "all" && c.type !== typeFilter) return false;
      if (
        q &&
        !c.name.toLowerCase().includes(q) &&
        !(c.slug ?? "").toLowerCase().includes(q) &&
        !(c.subcategory ?? "").toLowerCase().includes(q)
      )
        return false;
      return true;
    });
    return sortByCoachCount(matches, sort, direction);
  }, [ccos, query, statusFilter, typeFilter, sort, direction]);

  const changeSort = (column: CoachCountSort): void => {
    navigate({ search: nextCoachCountSortSearch({ sort, direction }, column) });
  };

  const createFields = (
    <>
      <TitleSlugFields
        create={create}
        idPrefix="cco"
        titleLabel="Name"
        title={form.name}
        slug={form.slug}
        titlePlaceholder="e.g., International Coaching Federation"
        slugPlaceholder="e.g., icf"
        slugRequired
        onTitleChange={(val) => setFormField("name", val)}
        onSlugChange={(val) => setFormField("slug", val)}
      />
      <div>
        <Label htmlFor="cco-type" className="mb-1">
          Type
        </Label>
        <Select
          value={form.type}
          onValueChange={(val) => setFormField("type", val as "pao" | "cto")}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cto">CTO</SelectItem>
            <SelectItem value="pao">PAO</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DescriptionField
        id="cco-description"
        value={form.description}
        placeholder="Brief description of this organization..."
        optional
        onChange={(val) => setFormField("description", val)}
      />
    </>
  );

  return (
    <AdminListPage
      title="Organizations"
      description="Coach Certifying Organizations — content editor"
      itemName="Organization"
      create={create}
      createFields={createFields}
      onCreateSubmit={handleSubmit}
      canCreate={Boolean(form.name.trim() && form.slug.trim())}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      summary={
        <div className="mb-6 grid grid-cols-3 gap-4">
          <Card>
            <div className="text-2xl font-medium text-gray-900">
              {totalCount}
            </div>
            <div className="text-xs text-gray-500">Total CCOs</div>
          </Card>
          <Card>
            <div className="text-2xl font-medium text-gray-900">{paoCount}</div>
            <div className="text-xs text-gray-500">PAOs</div>
          </Card>
          <Card>
            <div className="text-2xl font-medium text-gray-900">{ctoCount}</div>
            <div className="text-xs text-gray-500">CTOs</div>
          </Card>
        </div>
      }
      statusFilter={{ value: statusFilter, onChange: setStatusFilter }}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search organizations...",
        extras: (
          <Select
            value={typeFilter}
            onValueChange={(val) => setTypeFilter(val as TypeFilterValue)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownCcos}
        columns={CCO_COLUMNS}
        sorts={COACH_COUNT_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(ccos && ccos.length > 0),
          icon: Building2,
          itemName: "Organization",
          title: "No organizations yet",
          description: "Create a coaching organization to get started.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}
